// Rung 0, and rung 1 when a second address turns up.
//
// This is the whole application for a restaurant that never grows. An
// address, who is driving, send. No route, no roster, no board, no
// vocabulary anybody has to learn — and nothing on this screen says
// "fleet".
//
// A second address is not a feature announcement. The stops get ordered,
// the sequence appears, and the app says that it did the ordering. That
// is rung 1 in its entirety.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  THREAD_MODE,
  coverageOf,
  granularityDefault,
  offGraphMessage,
  profileFor,
  type ProfileId
} from "@wayfind/core";
import { Field, Note, Panel } from "./Bits";
import { PlaceField } from "./PlaceField";
import { StopFields, stopSummary } from "./StopFields";
import { planInputFor, planKeyFor, type Dispatcher, type Ordering } from "../ops/dispatch";
import { localId, type FleetState, type StoredJob, type StoredStop } from "../ops/state";
import { distanceWords, durationWords, outcomeVocabulary, plural, type Vocabulary } from "../ops/words";
import { config, nowSeconds } from "../config";
import type { LadderView } from "../ops/ladder";

/** Who is taking it. Three answers, and the third only exists once there are drivers. */
export type DriverChoice =
  | { kind: "self" }
  | { kind: "device"; id: string }
  | { kind: "none" };

const DEFAULT_TTL_SECONDS = 8 * 3600;

export function Compose({
  state,
  vocabulary,
  ladder,
  dispatcher,
  onPairPhone,
  onSent
}: {
  state: FleetState;
  vocabulary: Vocabulary;
  ladder: LadderView;
  dispatcher: Dispatcher | null;
  onPairPhone(): void;
  onSent(job: StoredJob): void;
}): JSX.Element {
  const profile = state.profile ?? "courier";
  const [stops, setStops] = useState<StoredStop[]>([]);
  const [driver, setDriver] = useState<DriverChoice>({ kind: "self" });
  const [ordering, setOrdering] = useState<Ordering | null>(null);
  const [handSorted, setHandSorted] = useState(false);
  const [readyIn, setReadyIn] = useState("");
  const [mode, setMode] = useState<number>(granularityDefault(profile).mode);
  const [showGranularity, setShowGranularity] = useState(false);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const devices = useMemo(
    () => state.devices.filter(device => device.retiredUnixSeconds == null),
    [state.devices]
  );

  useEffect(() => { setMode(granularityDefault(profile).mode); }, [profile]);

  // The order the round should be driven in, from this device's own
  // graph. Recomputed whenever the set of places changes and thrown away
  // the moment the operator sorts it themselves.
  useEffect(() => {
    if (handSorted || !dispatcher || stops.length < 2) { setOrdering(null); return; }
    let live = true;
    void dispatcher.order(stops).then(result => { if (live) setOrdering(result); });
    return () => { live = false; };
  }, [dispatcher, stops, handSorted]);

  const sequence = useMemo(() => {
    if (handSorted || !ordering) return stops;
    return ordering.order.map(index => stops[index]).filter((stop): stop is StoredStop => stop != null);
  }, [stops, ordering, handSorted]);

  const near = stops.length ? { lat: stops[stops.length - 1]!.lat, lon: stops[stops.length - 1]!.lon } : null;

  /**
   * Adds a stop, once the routing graph agrees it can reach it.
   *
   * The search index covers a continent and the routing graph covers one
   * region, and nothing used to connect those two facts: an address the
   * dispatcher could find was an address they could dispatch to. A stop
   * outside the graph produced a ticket that was well-formed and signed and
   * unroutable, and the first thing that noticed was a driver's phone hours
   * later — which could only report an epoch mismatch, naming the symptom.
   *
   * The stop is added either way. Refusal is the console's job at *send*,
   * where the whole plan is visible; here the point is that the dispatcher
   * learns it now, next to the address, while fixing it is still free.
   */
  const addStop = useCallback(async (place: { label: string; lat: number; lon: number }) => {
    setStops(current => [...current, { id: localId("stop"), ...place, fields: {} }]);
    const engine = dispatcher?.engine;
    if (!engine) return;
    const coverage = await coverageOf(engine, place);
    // `unknown` is a graph that could not be asked, not a graph that said no.
    // A network blink must not put a red sentence under a real address.
    if (coverage.covered || coverage.unknown) return;
    setProblem(offGraphMessage(place.label, config.regionName || undefined));
  }, [dispatcher]);

  const send = useCallback(async () => {
    if (!dispatcher || !sequence.length) return;
    setSending(true);
    setProblem(null);
    try {
      const recipients: string[] = [];
      let deviceId: string | null = null;
      let driverName: string | null = null;
      if (driver.kind === "device") {
        const device = devices.find(entry => entry.id === driver.id);
        if (!device) throw new Error("That phone is no longer paired.");
        recipients.push(device.publicKeyHex);
        deviceId = device.id;
        driverName = device.name;
      }
      // The console seals to itself as well, always. §20.9 is explicit
      // that this is a choice and never a default the library makes — a
      // dispatcher that skips it has minted a job it can no longer open,
      // and a dispatcher that cannot open what it sent cannot cancel it,
      // reassign it, or open a doorstep photo.
      recipients.push(state.consoleDevice.publicKeyHex);

      const plan = planInputFor(profile, sequence, {
        travelMode: 1,
        readyInMinutes: readyIn.trim() ? Number(readyIn) : null
      });
      const planBytes = dispatcher.planBytes(plan);
      const notAfter = nowSeconds() + DEFAULT_TTL_SECONDS;
      const issued = await dispatcher.issueJob({
        issuerSeedHex: state.issuerSeedHex,
        plan,
        recipients,
        notAfter,
        mode,
        // An enrolled seed wins; the configured one is the fallback for a
        // deployment that already knows its own keeper. Null only when
        // there is genuinely nothing to dial.
        bootstrap: state.seed?.addresses ?? (config.meshBootstrap.length ? config.meshBootstrap : null),
        hintCustomerLink: state.seed?.hintCustomerLinks ?? false,
        trackBaseUrl: config.trackBaseUrl
      });

      const usedRungs: StoredJob["usedRungs"] = [];
      if (sequence.length >= 2) usedRungs.push(1);
      if (deviceId && devices.length >= 2) usedRungs.push(2);
      if (state.seed) usedRungs.push(5);

      onSent({
        id: localId("job"),
        createdUnixSeconds: nowSeconds(),
        profile,
        stops: sequence,
        planKey: planKeyFor(sequence),
        planRefHex: dispatcher.planRefHex(planBytes),
        jobIdHex: issued.jobIdHex,
        deviceId,
        driverName: driver.kind === "self" ? "Me" : driverName,
        driverless: driver.kind === "none",
        heldBy: driver.kind === "self" ? "self" : deviceId,
        mode,
        travelMode: 1,
        notAfter,
        trackUrl: issued.trackUrl,
        driverUrl: issued.driverUrl,
        sealedB64: issued.sealedB64,
        optimized: !handSorted && ordering != null && sequence.length > 2,
        totalMeters: ordering?.totalMeters ?? null,
        startedUnixSeconds: null,
        finishedUnixSeconds: null,
        usedRungs,
        routeId: null,
        serviceDay: null,
        offerId: null
      });
      setStops([]);
      setHandSorted(false);
      setReadyIn("");
      setDriver({ kind: "self" });
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  }, [
    dispatcher, sequence, driver, devices, state.consoleDevice.publicKeyHex, state.issuerSeedHex,
    state.seed, profile, readyIn, mode, handSorted, ordering, onSent
  ]);

  const many = stops.length > 1;
  const granularity = granularityDefault(profile);
  const coarse = mode === THREAD_MODE.COARSE;

  return (
    <div className="stack">
      <Panel eyebrow={many ? `The ${vocabulary.run}` : "Where is it going"}>
        <PlaceField
          label={many ? `Add a ${vocabulary.stop}` : "Address"}
          autoFocus
          near={near}
          onPick={addStop}
        />

        {stops.length ? (
          <div className="stops" style={{ marginTop: "0.7rem" }}>
            {sequence.map((stop, index) => (
              <StopRow
                key={stop.id}
                index={index}
                count={sequence.length}
                profile={profile}
                stop={stop}
                showNumbers={many}
                onChange={fields => setStops(current =>
                  current.map(entry => (entry.id === stop.id ? { ...entry, fields } : entry)))}
                onRemove={() => setStops(current => current.filter(entry => entry.id !== stop.id))}
                onMove={delta => {
                  setHandSorted(true);
                  setStops(() => {
                    const next = [...sequence];
                    const target = index + delta;
                    if (target < 0 || target >= next.length) return next;
                    const [moved] = next.splice(index, 1);
                    if (moved) next.splice(target, 0, moved);
                    return next;
                  });
                }}
              />
            ))}
          </div>
        ) : null}

        {many && ordering && !handSorted ? (
          <div style={{ marginTop: "0.7rem" }}>
            <Note title="This is the app’s order, not yours." tone="quiet">
              The {plural(sequence.length, vocabulary.stop)} are arranged to be driven in the
              fewest minutes — {durationWords(ordering.totalSeconds)}
              {ordering.totalMeters > 0 ? ` and ${distanceWords(ordering.totalMeters)}` : ""}, worked
              out on this machine’s own map. Move any one of them and the app stops rearranging.
            </Note>
          </div>
        ) : null}

        {many && handSorted ? (
          <div className="row" style={{ marginTop: "0.7rem" }}>
            <span className="small muted">This is your order.</span>
            <button type="button" className="button button--small" onClick={() => setHandSorted(false)}>
              Let the app order it again
            </button>
          </div>
        ) : null}
      </Panel>

      {profile === "restaurant" ? (
        <Panel eyebrow="When it will be ready">
          <Field
            label="Ready in"
            hint={
              "The rider goes out before the food is bagged, so the customer’s link can go with the "
              + "order confirmation. The minute you put here rides in the plan, which is what lets "
              + "their own phone work out an arrival rather than take our word for it."
            }
          >
            <div className="row row--tight">
              <input
                className="input"
                style={{ maxWidth: "7rem" }}
                type="number"
                min={0}
                max={180}
                inputMode="numeric"
                placeholder="10"
                value={readyIn}
                onChange={event => setReadyIn(event.target.value)}
              />
              <span className="small muted">minutes</span>
            </div>
          </Field>
        </Panel>
      ) : null}

      <Panel eyebrow={`Who is driving`}>
        <div className="picker picker--two">
          <button
            type="button"
            className="picker__option"
            aria-pressed={driver.kind === "self"}
            onClick={() => setDriver({ kind: "self" })}
          >
            <span className="picker__title">Just me</span>
            <span className="picker__blurb">
              Sealed to this console. Open it on your own phone once that phone is paired.
            </span>
          </button>

          {devices.map(device => (
            <button
              key={device.id}
              type="button"
              className="picker__option"
              aria-pressed={driver.kind === "device" && driver.id === device.id}
              onClick={() => setDriver({ kind: "device", id: device.id })}
            >
              <span className="picker__title">{device.name}</span>
              <span className="picker__blurb">
                Sealed to this phone and no other. <code className="fingerprint wf-code">{device.fingerprint}</code>
              </span>
            </button>
          ))}

          <button type="button" className="picker__option" onClick={onPairPhone}>
            <span className="picker__title">Another phone…</span>
            <span className="picker__blurb">
              Pair it once and jobs can be sealed to it. It takes the code the phone shows.
            </span>
          </button>

          {devices.length >= 1 ? (
            <button
              type="button"
              className="picker__option"
              aria-pressed={driver.kind === "none"}
              onClick={() => setDriver({ kind: "none" })}
            >
              <span className="picker__title">Nobody free right now</span>
              <span className="picker__blurb">
                It goes out anyway: the {vocabulary.recipient} gets their link, and you hand the work
                over when someone comes back.
              </span>
            </button>
          ) : null}
        </div>
      </Panel>

      {profile === "pharmacy" ? <AtTheDoor stops={stops} /> : null}

      <Panel eyebrow="What the customer will see" tone="quiet">
        {granularity.deliberateOverride && !coarse ? (
          <div className="banner banner--halt" role="note">
            <span className="banner__mark">Read this</span>
            <p className="banner__text">
              You have turned on a live position for a vehicle carrying children. Anyone who is
              forwarded this link — a parent, a group chat, whoever a phone gets left unlocked next
              to — can watch the {vocabulary.run} move in real time for as long as the link lasts.{" "}
              {profileFor(profile).granularityRationale}
            </p>
          </div>
        ) : (
          <Note
            title={coarse
              ? `${vocabulary.Recipient}s see the ${vocabulary.stop}s, never the vehicle.`
              : `${vocabulary.Recipient}s see where the ${vocabulary.driver} is.`}
            tone={coarse ? "quiet" : "signal"}
          >
            {coarse ? profileFor(profile).granularityRationale : granularity.rationale}
          </Note>
        )}

        {showGranularity ? (
          <div className="row" style={{ marginTop: "0.6rem" }}>
            <button
              type="button"
              className="button button--small"
              aria-pressed={coarse}
              onClick={() => setMode(THREAD_MODE.COARSE)}
            >
              {vocabulary.Stop}s only
            </button>
            <button
              type="button"
              className="button button--small"
              aria-pressed={!coarse}
              onClick={() => setMode(THREAD_MODE.FINE)}
            >
              Live position
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="button button--small button--ghost"
            style={{ marginTop: "0.6rem" }}
            onClick={() => setShowGranularity(true)}
          >
            Change this
          </button>
        )}
      </Panel>

      {problem ? (
        <div className="banner banner--halt" role="alert">
          <span className="banner__mark">No</span>
          <p className="banner__text">{problem}</p>
        </div>
      ) : null}

      <div className="row row--end">
        <button
          type="button"
          className="button button--primary"
          disabled={!dispatcher || !stops.length || sending}
          onClick={() => { void send(); }}
        >
          {sending ? "Sealing…" : many ? `Send the ${vocabulary.run}` : "Send it"}
        </button>
      </div>

      {ladder.reached === 0 && !state.jobs.length ? (
        <p className="footer" style={{ marginTop: 0, paddingTop: 0 }}>
          That is the whole application. Everything else this product can do appears the day the work
          needs it, and not before.
        </p>
      ) : null}
    </div>
  );
}

/**
 * What the driver will be able to say at the door, before they get there.
 *
 * A pharmacy's record is the thing that settles a regulator's question,
 * and the outcomes are not interchangeable: "nobody home, returned to the
 * pharmacy" and "left with a neighbour" are different records, and the
 * second one must be impossible for an item that needs an identity check.
 * The rule lives on the driver's device, where the tap happens — this
 * panel is the dispatcher seeing the consequence of the box they ticked.
 */
function AtTheDoor({ stops }: { stops: StoredStop[] }): JSX.Element {
  const anyIdCheck = stops.some(stop => stop.fields.identityCheck === true);
  return (
    <Panel eyebrow="What the driver will be able to say" tone="quiet">
      <ul className="rows" style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {outcomeVocabulary("pharmacy").map(outcome => {
          const blocked = anyIdCheck && outcome.forbiddenWhen === "identityCheck";
          return (
            <li key={outcome.text} className="row row--tight" style={{ fontSize: "var(--wf-size-small)" }}>
              <span
                className={blocked ? "muted" : ""}
                style={blocked ? { textDecoration: "line-through" } : undefined}
              >
                {outcome.text}
              </span>
              {blocked ? (
                <span className="small muted">— off the table: this one needs an identity check</span>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="field__hint" style={{ marginTop: "0.5rem" }}>
        Every one of these is a signed record the driver asserts, not something the app infers from
        a position. Nothing here is “Delivery exception code 4”.
      </p>
    </Panel>
  );
}

function StopRow({
  index,
  count,
  profile,
  stop,
  showNumbers,
  onChange,
  onRemove,
  onMove
}: {
  index: number;
  count: number;
  profile: ProfileId;
  stop: StoredStop;
  showNumbers: boolean;
  onChange(fields: StoredStop["fields"]): void;
  onRemove(): void;
  onMove(delta: number): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const summary = stopSummary(profile, stop);
  return (
    <div className="stoprow">
      <span className="stoprow__index">{showNumbers ? index + 1 : "→"}</span>
      <div>
        <p className="stoprow__label">{stop.label}</p>
        {summary && !open ? <p className="stoprow__meta">{summary}</p> : null}
      </div>
      <div className="stoprow__tools">
        {showNumbers ? (
          <>
            <button
              type="button"
              className="icon-button"
              aria-label="Move earlier"
              disabled={index === 0}
              onClick={() => onMove(-1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="icon-button"
              aria-label="Move later"
              disabled={index === count - 1}
              onClick={() => onMove(1)}
            >
              ↓
            </button>
          </>
        ) : null}
        <button
          type="button"
          className="icon-button"
          aria-label={open ? "Hide details" : "Add details"}
          aria-expanded={open}
          onClick={() => setOpen(current => !current)}
        >
          {open ? "−" : "+"}
        </button>
        <button type="button" className="icon-button" aria-label="Remove" onClick={onRemove}>
          ×
        </button>
      </div>
      {open ? (
        <div className="stoprow__fields">
          <StopFields profile={profile} stop={stop} onChange={onChange} />
        </div>
      ) : null}
    </div>
  );
}
